import { useMutation, useQuery } from '@tanstack/react-query'
import { Field, FieldAttributes, Form, Formik } from 'formik'
import { useState } from 'react'
import { parseBackend } from 'src/backend'
import { OperationType } from 'src/BackendProvider'
import { DatePicker } from 'src/components/input/DatePicker'
import { useSingleTeamMangement } from 'src/Management'
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/20/solid";
import clsx from 'clsx'

type PRCredentials = {
  configId?: number
  username: string
  password: string
  host: string
  realm: string
  startDate?: string
}

type PrAdminRequest = {
  operation: OperationType,
  realm?: string,
  config?: PRCredentials,
}

function usePrCredentials(realm: string) {
  const queryKey = ['pr', 'credentials', realm]

  const {data: credentials} = useQuery({
    queryKey,
    queryFn: () =>
      parseBackend(
        fetch('/api/patent-renewal/admin', {
          method: 'POST',
          body: JSON.stringify({ realm, operation: 'get' } as PrAdminRequest),
          headers: { 'Content-Type': 'application/json' },
        }),
      ).then(resp => resp[0]),
      enabled: !!realm,
  })

  const {mutateAsync} = useMutation({mutationKey: queryKey, mutationFn: (payload: PRCredentials) => {
    const operation = payload.configId === undefined ? 'add' : 'update'
    const body = JSON.stringify({config: payload, operation} as PrAdminRequest)
    return fetch('/api/patent-renewal/admin', {
      method: 'POST',
      body,
      headers: { 'Content-Type': 'application/json' },
    })

  }})

  return {credentials, updateCredentials: mutateAsync}
}

export default function Credentials() {
  const { team } = useSingleTeamMangement()

  const { credentials, updateCredentials } = usePrCredentials(team?.realm)

  if (!team?.realm) return null
  
  const initialValues = {
    configId: undefined,
    username: '',
    password: '',
    host: 'https://api.patentrenewal.com',
    realm: team.realm,
    startDate: undefined,
    ...credentials,
  }

  console.log({initialValues})

  async function onSubmit(values) {
    await updateCredentials(values)
  }

  const fields = [
    { name: 'username', label: 'Username', type: 'text' },
    { name: 'password', label: 'Password', type: 'password' },
    { name: 'host', label: 'Host', type: 'text' },
  ]

  return (
    <div className='p-4'>

    <Formik {...{ initialValues, onSubmit, enableReinitialize: true }}>{({resetForm }) => (
      <Form className='space-y-4 text-sm rounded-lg bg-white shadow w-full sm:w-fit p-4'>
        {fields.map(field => (
          <label key={field.name} className='block'>
            <div className='label mb-1'>{field.label}</div>
            {field.type === 'password' 
              ? <PasswordField name={field.name} type={field.type} className='form-input text-sm sm:min-w-md' /> 
              : <Field name={field.name} type={field.type} className='form-input text-sm sm:min-w-md' />}
          </label>
        ))}
        <label className='block'>
          <div className='label mb-1'>Start Date</div>
          <Field name='startDate' as={DatePicker} className='form-input text-sm sm:min-w-md' />
        </label>
        <div className='flex flex-row-reverse gap-4'>
          <button type='submit' className='btn-primary'>Save</button>
          <button type='button' className='btn-secondary' onClick={() => resetForm()}>Reset</button>
          {/* TODO reset button with dirty flow  */}
        </div>
      </Form>
    )}</Formik>
    </div>
  )
}

function PasswordField(props: FieldAttributes<any>) {
  const [showPassword, setShowPassword] = useState(false)

  return (
    <div className={clsx('flex flex-row focus-within:ring-pcx-400 focus-within:ring-1', props.className)}>
      <Field {...props} type={showPassword ? 'text' : props.type} className={'grow focus:outline-none'} />
      <button type="button" className="shrink-0 w-9 h-max" onClick={() => setShowPassword(!showPassword)}>
        {showPassword ? (
          <EyeSlashIcon title="Hide Password" className="h-5 w-5 inline" />
        ) : (
          <EyeIcon title="Show Password" className="h-5 w-5 inline" />
        )}
      </button>
    </div>
  )
}
